import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  message, 
  Popconfirm,
  Spin,
  Row,
  Col
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  UserOutlined,
  PhoneOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { getCustomers, updateCustomer, deleteCustomer } from '../services/customerService';
import { formatDateTime } from '../utils/dateUtils';

const { Title, Text } = Typography;

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [form] = Form.useForm();
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchForm] = Form.useForm();
  
  // 加载客户列表
  const loadCustomers = async (params = {}) => {
    setLoading(true);
    try {
      // 合并搜索条件和分页参数
      const queryParams = {
        ...params,
        page: pagination.current,
        pageSize: pagination.pageSize
      };
      
      const data = await getCustomers(queryParams);
      console.log('Received customer data:', data);
      
      // 正确处理返回的数据结构
      if (Array.isArray(data)) {
        setCustomers(data);
        setPagination({
          ...pagination,
          total: data.length
        });
      } else {
        setCustomers([]);
        setPagination({
          ...pagination,
          total: 0
        });
      }
    } catch (error) {
      message.error(error.message || '获取客户列表失败');
    } finally {
      setLoading(false);
    }
  };
  
  // 组件加载时获取客户列表
  useEffect(() => {
    loadCustomers();
  }, []);
  
  // 处理表格分页变化
  const handleTableChange = (pagination) => {
    setPagination(pagination);
    const searchValues = searchForm.getFieldsValue();
    loadCustomers(searchValues);
  };
  
  // 处理搜索
  const handleSearch = (values) => {
    setPagination({ ...pagination, current: 1 });
    loadCustomers(values);
  };
  
  // 打开创建客户模态框
  const showCreateModal = () => {
    setModalTitle('添加客户');
    setEditingCustomer(null);
    form.resetFields();
    setModalVisible(true);
  };
  
  // 打开编辑客户模态框
  const showEditModal = (customer) => {
    setModalTitle('编辑客户');
    setEditingCustomer(customer);
    form.setFieldsValue({
      name: customer.name,
      phone: customer.phone,
      address: customer.address
    });
    setModalVisible(true);
  };
  
  // 关闭模态框
  const handleCancel = () => {
    setModalVisible(false);
  };
  
  // 处理表单提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // 确保必要字段存在
      if (!values.name || !values.name.trim()) {
        message.error('请输入客户姓名');
        return;
      }
      
      if (!values.address || !values.address.trim()) {
        message.error('请输入地址');
        return;
      }
      
      // 电话号码可以为空，不需要检查
      
      if (editingCustomer) {
        // 更新客户
        await updateCustomer(editingCustomer.id, values);
        message.success('客户更新成功');
      } else {
        // 创建客户 (使用null作为id参数表示新客户)
        await updateCustomer(null, values);
        message.success('客户添加成功');
      }
      
      setModalVisible(false);
      loadCustomers(searchForm.getFieldsValue());
    } catch (error) {
      message.error(error.message || '操作失败');
    }
  };
  
  // 处理删除客户
  const handleDelete = async (id) => {
    try {
      await deleteCustomer(id);
      message.success('客户删除成功');
      loadCustomers(searchForm.getFieldsValue());
    } catch (error) {
      message.error(error.message || '删除客户失败');
    }
  };
  
  // 格式化电话号码显示
  const formatPhoneDisplay = (phone) => {
    if (!phone) return '-';
    
    // 判断是否是生成的唯一ID格式
    if (phone.startsWith('CUST-')) {
      return '未提供电话';
    }
    
    return phone;
  };

  // 表格列定义
  const columns = [
    {
      title: '客户姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => formatPhoneDisplay(text),
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
    },
    {
      title: '最近更新',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (timestamp) => formatDateTime(timestamp),
    },
    {
      title: '运单数量',
      dataIndex: 'shipment_count',
      key: 'shipment_count',
      render: (count) => count || 0,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => showEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除此客户吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  
  return (
    <div className="customers-page">
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={4} style={{ margin: 0 }}>客户管理</Title>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={showCreateModal}
            >
              添加客户
            </Button>
          </div>
        }
      >
        <Form
          form={searchForm}
          layout="vertical"
          onFinish={handleSearch}
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="name" label="客户姓名">
                <Input placeholder="输入客户姓名" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="phone" label="联系电话">
                <Input placeholder="输入联系电话" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<SearchOutlined />}
                  style={{ marginTop: 29 }}
                >
                  搜索
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        
        <Spin spinning={loading}>
          <Table 
            columns={columns} 
            dataSource={customers} 
            rowKey="phone"
            pagination={pagination}
            onChange={handleTableChange}
          />
        </Spin>
      </Card>
      
      <Modal
        title={modalTitle}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="客户姓名"
            rules={[{ required: true, message: '请输入客户姓名' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="客户姓名" />
          </Form.Item>
          
          <Form.Item
            name="phone"
            label="联系电话"
            rules={[{ required: false }]}
            help="如果客户没有提供电话，请点击左侧即‘无电话’按钮"
          >
            <Input.Group compact>
              {!editingCustomer && (
                <Button 
                  onClick={() => {
                    form.setFieldsValue({ phone: '' });
                    setTimeout(() => form.validateFields(['phone']), 100);
                  }}
                  style={{ width: '25%' }}
                >
                  无电话
                </Button>
              )}
              <Input 
                prefix={<PhoneOutlined />} 
                placeholder="联系电话" 
                style={{ width: editingCustomer ? '100%' : '75%' }}
              />
            </Input.Group>
          </Form.Item>
          
          <Form.Item
            name="address"
            label="地址"
            rules={[{ required: true, message: '请输入地址' }]}
          >
            <Input prefix={<HomeOutlined />} placeholder="地址" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Customers;
